import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  faCheckCircle,
  faCircleInfo,
  faDownload,
  faSpinnerThird,
} from '@fortawesome/pro-solid-svg-icons'
import { clsx } from 'clsx'

import { Alert } from 'Components/_theme/Alert/Alert'
import { ReportItemStatusEnum } from 'Components/DownloadDataErasureReportsCard/DownloadDataErasureReportsCard.types'
import type { DownloadDateErasureReportsCardItemProps } from 'Components/DownloadDataErasureReportsCard/DownloadDateErasureReportsCardItem.types'
import { Icon } from 'Components/Icon/Icon'

import { buttonize } from 'Portal/src/utilities'

export const DownloadDateErasureReportsCardItem = (
  props: DownloadDateErasureReportsCardItemProps,
) => {
  const { report, onReportRequest, onReportDownload, hasPendingReports } = props

  const intl = useIntl()

  const isDisabled = useMemo(() => {
    if (report.status === ReportItemStatusEnum.PENDING) {
      return true
    }

    if (report.status === ReportItemStatusEnum.AVAILABLE) {
      return false
    }

    return hasPendingReports
  }, [hasPendingReports, report.status])

  const handleReportClick = useCallback(() => {
    if (report.status === ReportItemStatusEnum.NOT_AVAILABLE) {
      onReportRequest(report.year)
    }

    if (report.status === ReportItemStatusEnum.AVAILABLE) {
      onReportDownload(report.url)
    }
  }, [
    onReportDownload,
    onReportRequest,
    report.status,
    report.url,
    report.year,
  ])

  return (
    <div
      key={report.year}
      className={clsx('DownloadDataErasureReportsCardItem', {
        'DownloadDataErasureReportsCardItem--isInteractive': !isDisabled,
      })}
      {...(!isDisabled && buttonize(handleReportClick))}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          className={clsx('text-secondary fw-semibold', {
            'opacity-50': isDisabled,
          })}
        >
          {intl.formatMessage(
            { id: 'DownloadDataErasureReportsCard_button_prefix' },
            { year: report.year },
          )}
        </div>

        <div>
          <Icon
            className={clsx({
              'opacity-50':
                isDisabled && report.status !== ReportItemStatusEnum.PENDING,
            })}
            icon={
              report.status === ReportItemStatusEnum.PENDING
                ? faSpinnerThird
                : faDownload
            }
            role="presentation"
            size={14}
            spin={report.status === ReportItemStatusEnum.PENDING}
          />
        </div>
      </div>

      {report.status === ReportItemStatusEnum.PENDING && (
        <Alert iconLeft={faCircleInfo} variant="info">
          <FormattedMessage id="DownloadDataErasureReportsCard_pending_reports" />
        </Alert>
      )}

      {report.status === ReportItemStatusEnum.AVAILABLE && report.url && (
        <Alert iconLeft={faCheckCircle} variant="success">
          <FormattedMessage id="DownloadDataErasureReportsCard_available_reports" />
        </Alert>
      )}
    </div>
  )
}
